import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  EventSubmissionModal,
  ExternalLink,
  LandingPageIntro,
  LayoutSingleColumn,
  Page,
  SecondaryButton,
} from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';

import { EVENTS_DATA, MONTH_OPTIONS } from './eventsData';
import { ALL_CANADA_OPTION, CANADIAN_PROVINCE_OPTIONS } from './provinces';
import css from './EventsPage.module.css';

const TBA_MONTH_KEY = '2026-12';

const parseISODate = isoDate => {
  const [year, month, day] = isoDate.split('-').map(value => Number(value));
  return new Date(Date.UTC(year, month - 1, day));
};

const formatRange = (startDate, endDate) => {
  const start = parseISODate(startDate);
  const end = parseISODate(endDate);
  const sameDay = startDate === endDate;

  const startLabel = start.toLocaleDateString('en-CA', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });

  if (sameDay) {
    return startLabel;
  }

  const endLabel = end.toLocaleDateString('en-CA', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return `${startLabel} to ${endLabel}`;
};

const sortEventsChronologically = events => {
  return [...events].sort((a, b) => {
    if (a.dateTba && !b.dateTba) {
      return 1;
    }
    if (!a.dateTba && b.dateTba) {
      return -1;
    }
    if (a.dateTba && b.dateTba) {
      return a.name.localeCompare(b.name);
    }

    const aStart = parseISODate(a.startDate).getTime();
    const bStart = parseISODate(b.startDate).getTime();
    return aStart - bStart;
  });
};

const getMonthKeyFromISODate = isoDate => {
  const [year, month] = isoDate.split('-');
  return `${year}-${month}`;
};

const EVENT_IMAGE_FALLBACK = '/static/events/event-image-placeholder.svg';

const EventsPage = () => {
  const dispatch = useDispatch();
  const scrollingDisabled = useSelector(isScrollingDisabled);
  const [selectedProvince, setSelectedProvince] = useState(ALL_CANADA_OPTION.value);
  const [isSubmissionModalOpen, setSubmissionModalOpen] = useState(false);

  const sortedEvents = useMemo(() => sortEventsChronologically(EVENTS_DATA), []);
  const filteredEvents = useMemo(() => {
    return selectedProvince === ALL_CANADA_OPTION.value
      ? sortedEvents
      : sortedEvents.filter(event => event.province === selectedProvince);
  }, [selectedProvince, sortedEvents]);

  const groupedEvents = useMemo(() => {
    const grouped = MONTH_OPTIONS.reduce((acc, month) => ({ ...acc, [month.key]: [] }), {});

    filteredEvents.forEach(event => {
      const targetMonthKey = event.dateTba
        ? TBA_MONTH_KEY
        : getMonthKeyFromISODate(event.startDate);

      if (grouped[targetMonthKey]) {
        grouped[targetMonthKey].push(event);
      }
    });

    return MONTH_OPTIONS.map(month => ({ ...month, events: grouped[month.key] || [] })).filter(
      month => month.events.length > 0
    );
  }, [filteredEvents]);

  const hasFilteredEvents = filteredEvents.length > 0;

  return (
    <Page
      title="Bookish Events"
      description="Book fairs, author events, signings and literary festivals worth putting on your calendar."
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main className={css.root}>
          <LandingPageIntro
            title="Bookish Events"
            description="Book fairs, author events, signings and literary festivals worth putting on your calendar."
          />

          <div className={css.submitEventRow}>
            <SecondaryButton
              className={css.submitEventButton}
              type="button"
              onClick={() => setSubmissionModalOpen(true)}
            >
              Submit an event
            </SecondaryButton>
          </div>

          <EventSubmissionModal
            isOpen={isSubmissionModalOpen}
            onClose={() => setSubmissionModalOpen(false)}
            onManageDisableScrolling={(id, disableScrolling) =>
              dispatch(manageDisableScrolling(id, disableScrolling))
            }
          />

          <section className={css.filterSection} aria-label="Filter events">
            <div className={css.filterRow}>
              <label className={css.filterControl} htmlFor="events-province-filter">
                <span>Province</span>
                <select
                  id="events-province-filter"
                  value={selectedProvince}
                  onChange={event => setSelectedProvince(event.target.value)}
                >
                  {CANADIAN_PROVINCE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </section>

          <section className={css.comingUpSection} aria-labelledby="coming-up-title">
            <div className={css.sectionHeading}>
              <h2 id="coming-up-title">Coming Up</h2>
            </div>

            {hasFilteredEvents ? (
              <div className={css.monthSections}>
                {groupedEvents.map(month => {
                  return (
                    <section
                      key={month.key}
                      className={css.monthSection}
                      aria-labelledby={`events-month-${month.key}`}
                    >
                      <div className={css.monthHeadingWrap}>
                        <h3 id={`events-month-${month.key}`} className={css.monthHeading}>
                          {month.label}
                        </h3>
                      </div>

                      <div className={css.monthGrid}>
                        {month.events.map(event => {
                          const dateLabel = event.dateTba
                            ? 'Dates TBA'
                            : formatRange(event.startDate, event.endDate);
                          const showAdmission = event.admission && event.admission !== 'TBA';

                          return (
                            <ExternalLink
                              key={event.id}
                              href={event.websiteUrl}
                              className={css.eventCard}
                              aria-label={event.name}
                            >
                              <div className={css.eventImageFrame}>
                                <img
                                  className={css.eventImage}
                                  src={event.image || EVENT_IMAGE_FALLBACK}
                                  alt={event.imageAlt}
                                  onError={event => {
                                    event.currentTarget.onerror = null;
                                    event.currentTarget.src = EVENT_IMAGE_FALLBACK;
                                  }}
                                />
                              </div>
                              <div className={css.eventContent}>
                                <p className={css.eventDate}>{dateLabel}</p>
                                <h4 className={css.eventName}>{event.name}</h4>
                                <p className={css.eventLocation}>
                                  {event.city}, {event.province}
                                </p>
                                <p className={css.eventType}>{event.eventType}</p>
                                <p className={css.eventDescription}>{event.description}</p>
                                <p className={css.eventVenue}>{event.venue}</p>
                                {showAdmission ? (
                                  <div className={css.eventFooterRow}>
                                    <span className={css.admissionPill}>{event.admission}</span>
                                  </div>
                                ) : null}
                              </div>
                            </ExternalLink>
                          );
                        })}
                      </div>
                    </section>
                  );
                })}
              </div>
            ) : (
              <div className={css.emptyState} role="status">
                <h3>Nothing on the PageHop calendar for this province yet.</h3>
                <p>Try another province or check back soon as new events are added.</p>
              </div>
            )}
          </section>
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

export { EventsPage };
export default EventsPage;
